import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  ExternalLink, Plus, Pencil, TrendingUp, Sparkles, Film, Eye,
  DollarSign, Loader2, ArrowUpRight, PenSquare, CheckCircle2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_HERO = "https://images.pexels.com/photos/9665187/pexels-photo-9665187.jpeg";

const heroUrl = (path) => (path ? `${BACKEND}/api/files/${path}` : DEFAULT_HERO);

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
  const units = [
    ["year", 31536000], ["month", 2592000], ["week", 604800],
    ["day", 86400], ["hour", 3600], ["minute", 60],
  ];
  for (const [name, s] of units) {
    const v = Math.floor(secs / s);
    if (v >= 1) return `${v} ${name}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function AuthedHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/home");
        if (alive) setData(res.data);
      } catch (err) {
        console.error("Failed to load home feed:", err);
        if (alive) setData({ top_vault: null, drafts: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="grid place-items-center h-[70vh]" data-testid="home-loading">
          <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
        </div>
      </div>
    );
  }

  const top = data?.top_vault;
  const drafts = data?.drafts || [];
  const finished = data?.finished || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="authed-home">
      <Header />
      {top ? <TopVaultHero vault={top} /> : <EmptyHero onCreate={() => navigate("/dashboard?new=1")} />}

      {/* Continue Editing */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16 pt-16 md:pt-24 relative z-20">
        <div className="flex items-center gap-2.5 mb-6">
          <PenSquare className="h-5 w-5 text-[#00F0FF]" />
          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="continue-editing-heading">
            Continue Editing
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {drafts.map((d, i) => (
            <DraftCard key={d.id} draft={d} index={i} onOpen={() => navigate(`/events/${d.id}`)} />
          ))}
          <NewProjectCard onClick={() => navigate("/dashboard?new=1")} />
        </div>
      </section>

      {/* Finished Vaults */}
      {finished.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24" data-testid="finished-vaults-section">
          <div className="flex items-center gap-2.5 mb-6">
            <CheckCircle2 className="h-5 w-5 text-[#00F0FF]" />
            <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="finished-vaults-heading">
              Finished Vaults
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {finished.map((v, i) => (
              <FinishedCard key={v.id} vault={v} index={i} onOpen={() => navigate(`/events/${v.id}`)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const TopVaultHero = ({ vault }) => {
  const a = vault.analytics || {};
  const pillText = vault.has_sales ? "TOP SELLING VAULT" : "FEATURED VAULT";

  return (
    <section className="relative grain overflow-hidden" data-testid="top-vault-hero">
      <img src={heroUrl(vault.hero_image_path)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/30" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 78% 32%, rgba(0,240,255,0.14), transparent 55%)" }} />

      {/* faint title watermark */}
      <div aria-hidden className="pointer-events-none absolute left-4 md:left-8 top-10 md:top-14 max-w-[70%]">
        <span className="font-display font-extrabold uppercase leading-none tracking-tight text-white/[0.04] text-[14vw] md:text-[9vw] line-clamp-2 break-words">
          {vault.title}
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 min-h-[72vh] md:min-h-[78vh] flex flex-col justify-end pb-20 md:pb-28 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span
            data-testid="top-vault-pill"
            className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-semibold tracking-wider px-3 py-1.5"
          >
            <TrendingUp className="h-3.5 w-3.5" /> {pillText}
          </span>

          <h1 data-testid="top-vault-title" className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            {vault.title}
          </h1>

          {vault.description ? (
            <p data-testid="top-vault-desc" className="mt-4 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              {vault.description}
            </p>
          ) : null}

          {/* stat chips */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
            <span className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-[#00F0FF]" /> {money(a.total_revenue)} earned</span>
            <span className="inline-flex items-center gap-1.5"><Film className="h-4 w-4 text-white/40" /> {a.videos_sold || 0} clips sold</span>
            <span className="inline-flex items-center gap-1.5"><Eye className="h-4 w-4 text-white/40" /> {a.total_views || 0} views</span>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/events/${vault.id}`}
              data-testid="open-vault-btn"
              className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300 cyan-glow"
            >
              <Pencil className="h-4 w-4" /> Open Vault
            </Link>
            {vault.share_slug ? (
              <a
                href={`/vault/${vault.share_slug}`}
                target="_blank"
                rel="noreferrer"
                data-testid="view-public-btn"
                className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur text-white rounded-full px-6 py-3 hover:bg-white/10 transition-colors"
              >
                <ExternalLink className="h-4 w-4" /> View Public Page
              </a>
            ) : null}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const EmptyHero = ({ onCreate }) => (
  <section className="relative grain overflow-hidden" data-testid="empty-hero">
    <img src={DEFAULT_HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40" />
    <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 72% 30%, rgba(0,240,255,0.12), transparent 55%)" }} />
    <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 min-h-[70vh] flex flex-col justify-center py-28">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-semibold tracking-wider px-3 py-1.5">
          <Sparkles className="h-3.5 w-3.5" /> GET STARTED
        </span>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
          Publish your first vault
        </h1>
        <p className="mt-4 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
          Upload the clips from your latest shoot into a private, streamable vault. Price them, share one link, and let clients unlock what they love.
        </p>
        <button
          onClick={onCreate}
          data-testid="empty-create-btn"
          className="mt-8 inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300 cyan-glow"
        >
          <Plus className="h-4 w-4" /> Create a vault
        </button>
      </motion.div>
    </div>
  </section>
);

const DraftCard = ({ draft, index, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    onClick={onOpen}
    data-testid={`draft-card-${draft.id}`}
    className="group text-left rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] hover:border-[#00F0FF]/40 transition-colors"
  >
    <div className="relative aspect-video overflow-hidden bg-white/5">
      {draft.hero_image_path ? (
        <img
          src={heroUrl(draft.hero_image_path)}
          alt=""
          className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        />
      ) : (
        <div className="h-full w-full grid place-items-center bg-gradient-to-br from-[#141414] to-[#0a0a0a]">
          <Film className="h-8 w-8 text-white/20" />
        </div>
      )}
      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur text-white/80 text-[11px] font-medium px-2.5 py-1 border border-white/10">
        <PenSquare className="h-3 w-3" /> Draft
      </span>
      <span className="absolute bottom-3 right-3 rounded-md bg-black/70 text-white/90 text-xs font-mono px-1.5 py-0.5">
        {draft.video_count} {draft.video_count === 1 ? "clip" : "clips"}
      </span>
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold truncate">{draft.title}</h3>
        <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#00F0FF] transition-colors shrink-0" />
      </div>
      <p className="mt-1 text-xs text-white/45">Created {relativeTime(draft.created_at)}</p>
    </div>
  </motion.button>
);

const FinishedCard = ({ vault, index, onOpen }) => {
  const a = vault.analytics || {};
  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      onClick={onOpen}
      data-testid={`finished-card-${vault.id}`}
      className="group text-left rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] hover:border-[#00F0FF]/40 transition-colors"
    >
      <div className="relative aspect-video overflow-hidden bg-white/5">
        {vault.hero_image_path ? (
          <img
            src={heroUrl(vault.hero_image_path)}
            alt=""
            className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
          />
        ) : (
          <div className="h-full w-full grid place-items-center bg-gradient-to-br from-[#141414] to-[#0a0a0a]">
            <Film className="h-8 w-8 text-white/20" />
          </div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#00F0FF]/15 backdrop-blur text-[#00F0FF] text-[11px] font-semibold px-2.5 py-1 border border-[#00F0FF]/30">
          <CheckCircle2 className="h-3 w-3" /> Live
        </span>
        <span className="absolute bottom-3 right-3 rounded-md bg-black/70 text-white/90 text-xs font-mono px-1.5 py-0.5">
          {vault.video_count} {vault.video_count === 1 ? "clip" : "clips"}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold truncate">{vault.title}</h3>
          <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-[#00F0FF] transition-colors shrink-0" />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/50">
          <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-[#00F0FF]" /> {money(a.total_revenue)}</span>
          <span className="inline-flex items-center gap-1"><Film className="h-3.5 w-3.5 text-white/40" /> {a.videos_sold || 0} sold</span>
          <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-white/40" /> {a.total_views || 0}</span>
        </div>
      </div>
    </motion.button>
  );
};

const NewProjectCard = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="new-project-card"
    className="group rounded-2xl border border-dashed border-white/15 bg-white/[0.02] hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/[0.04] transition-colors min-h-[220px] grid place-items-center"
  >
    <div className="flex flex-col items-center gap-3 text-white/50 group-hover:text-white transition-colors">
      <span className="grid place-items-center h-12 w-12 rounded-full border border-white/15 group-hover:border-[#00F0FF]/50 group-hover:bg-[#00F0FF]/10 transition-colors">
        <Plus className="h-6 w-6" />
      </span>
      <span className="font-medium">New Project</span>
    </div>
  </button>
);
